import { lngLatToXY, xyToLngLat } from '@core/logic';
import { useAppDispatch } from '@core/redux/hooks';
import type { RootState } from '@core/redux/interface';
import { projectSelectors } from '@projects/redux';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import type { MapBoxBoundingBox } from '@viewer3D/types';
import { useSelector } from 'react-redux';
import { useLazyGetPhotoQuery, useLazyGetSignedPhotoQuery } from '@core/redux/query';
import { setSelectedPhoto, togglePhotos } from '@photos/components/PhotosInViewport/state';
import { photoApiV1 } from '@deepup/apis';
import type { GeoPositionSolution, Photo } from '@photos/types';
import { timestampToIsoString } from '@core/logic/helper/timeFormatting';

const bboxCache: { [projectId: string]: MapBoxBoundingBox | undefined } = {};

export const useConnect = (): {
  boundingBox?: MapBoxBoundingBox;
  cameraGoto: (goto: { x: number; y: number }) => void;
  loadSearchedPhoto: (photoId: string) => Promise<void>;
} => {
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const dispatch = useAppDispatch();
  const [getPhotoQuery] = useLazyGetPhotoQuery();
  const [getSignedPhotoQuery] = useLazyGetSignedPhotoQuery();

  const loadSearchedPhoto = async (photoId: string) => {
    if (!projectId) return;
    try {
      const p = await getPhotoQuery({ photoId }).unwrap();
      if (p && p.projectId === projectId) {
        const mappedPhoto: Photo = {
          id: p.id,
          address: p.location?.place ?? null,
          comment: p.comments.at(0)?.text ?? null,
          deviceId: p.deviceId,
          geoPositionSolution: String(
            photoApiV1.GeolocationSolution[p.geoPositionSolution],
          ) as GeoPositionSolution,
          lat: p.location?.coordinate?.latitude ?? 0,
          long: p.location?.coordinate?.longitude ?? 0,
          projectId: p.projectId,
          url: '',
          recordedAt: timestampToIsoString(p.recordedAt || p.createdAt),
          createdAt: timestampToIsoString(p.createdAt),
          updatedAt: timestampToIsoString(p.lastModifiedAt || p.createdAt),
          category: p.categoryName ? { id: null, name: p.categoryName } : null,
        };

        const signedUrl = await getSignedPhotoQuery({ photoId }).unwrap();
        const photoWithUrl = { ...mappedPhoto, url: signedUrl?.url ?? '' };

        togglePhotos(true);
        setSelectedPhoto(photoWithUrl);

        if (
          mappedPhoto.lat !== undefined &&
          mappedPhoto.long !== undefined &&
          mappedPhoto.lat !== 0 &&
          mappedPhoto.long !== 0
        ) {
          const { x, y } = lngLatToXY({ lng: mappedPhoto.long, lat: mappedPhoto.lat });
          dispatch(viewer3dActions.cameraGoto({ x, y }));
        }
      }
    } catch (e) {
      // Photo not found or network error, ignore
      console.error(e);
    }
  };

  const boundingBox = useSelector((state: RootState) => {
    if (!projectId) return undefined;

    if (bboxCache[projectId]) return bboxCache[projectId];

    const utmBoundingBox = projectSelectors.boundingBox(projectId, 1000)(state);

    if (!utmBoundingBox) return undefined;

    try {
      const { minX, minY, maxX, maxY } = utmBoundingBox;
      const { lng: minLng, lat: minLat } = xyToLngLat({ x: minX, y: minY });
      const { lng: maxLng, lat: maxLat } = xyToLngLat({ x: maxX, y: maxY });
      bboxCache[projectId] = [minLng, minLat, maxLng, maxLat];
      return bboxCache[projectId];
    } catch (_e) {
      return;
    }
  });

  return {
    boundingBox,
    cameraGoto: ({ x, y }) => dispatch(viewer3dActions.cameraGoto({ x, y })),
    loadSearchedPhoto,
  };
};
